<template>
  <div>
    <b-card
      no-body
      class="mb-0 roles-permissions-card"
    >
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="mb-1 mb-md-0"
          >
            <h4 class="mb-0">
              Roles &amp; Permissions
            </h4>
            <small class="text-muted">
              {{ dbRoles.length }} roles &middot; {{ flatMenuItems.length }} menu items &middot;
              which menu each role can see, per the current sidebar config.
            </small>
          </b-col>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-md-end"
          >
            <b-form-input
              v-model="searchQuery"
              class="d-inline-block"
              style="max-width: 280px;"
              placeholder="Search menu..."
            />
          </b-col>
        </b-row>
      </div>

      <div class="matrix-scroll table-responsive">
        <b-table
          class="position-relative roles-matrix"
          :items="tableItems"
          :fields="tableFields"
          sticky-header="65vh"
          responsive
          show-empty
          empty-text="No matching records found"
        >
          <template #thead-top>
            <tr class="group-row">
              <th class="sticky-col group-cell" />
              <th
                v-for="group in roleGroups"
                :key="group.label"
                :colspan="group.roles.length"
                :class="`group-cell text-${group.variant}`"
              >
                {{ group.label }}
              </th>
            </tr>
          </template>

          <template #cell(menu)="data">
            <div class="menu-cell">
              <template v-if="data.value.includes(' > ')">
                <span class="menu-parent">{{ data.value.split(' > ')[0] }}</span>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="12"
                  class="mx-25 text-muted"
                />
                <span class="menu-child">{{ data.value.split(' > ')[1] }}</span>
              </template>
              <span
                v-else
                class="menu-parent"
              >{{ data.value }}</span>
            </div>
          </template>

          <template #cell()="data">
            <div class="text-center">
              <span
                v-if="data.value"
                class="role-check"
                :class="`role-check-${data.field.roleVariant}`"
                :title="data.field.label"
              >
                <feather-icon
                  icon="CheckIcon"
                  size="13"
                />
              </span>
              <span
                v-else
                class="text-muted"
              >&ndash;</span>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BTable, BRow, BCol, BFormInput,
} from 'bootstrap-vue'
import axios from 'axios'
import { useToast } from 'vue-toastification/composition'
import { getToken, getUserData } from '@/auth/utils'
import { PERMISSIONS, permissionsForRole } from '@/auth/permissions'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import sidebarItems from '@/navigation/vertical/sidebar'

// Roles are colour-coded by department so the header bands and the check
// marks below them read as one group at a glance, instead of 14 flat columns.
const ROLE_GROUPS = [
  { label: 'Admin & Leadership', variant: 'primary', systemNames: ['super-admin', 'admin', 'head-office', 'executive', 'area-manager', 'division-head', 'operasional-dir'] },
  { label: 'Finance', variant: 'warning', systemNames: ['finance', 'finance-supervisor', 'fa-coal-manager'] },
  { label: 'Operations', variant: 'success', systemNames: ['branch', 'lab-manager', 'shipment'] },
  { label: 'Legal', variant: 'danger', systemNames: ['legal'] },
  { label: 'Read-only', variant: 'info', systemNames: ['auditor'] },
]

const MENU_PERMISSIONS = {
  Dashboard: PERMISSIONS.VIEW_DASHBOARD,
  User: PERMISSIONS.VIEW_USERS,
  Branch: PERMISSIONS.VIEW_BRANCHES,
  Role: PERMISSIONS.VIEW_ROLES,
  'Roles & Permissions': PERMISSIONS.VIEW_ROLES,
  Contracts: PERMISSIONS.VIEW_CONTRACTS,
  Certificate: PERMISSIONS.VIEW_CERTIFICATES,
  Receipt: PERMISSIONS.VIEW_RECEIPTS,
  Invoice: PERMISSIONS.VIEW_INVOICES,
  'Void Invoice': PERMISSIONS.VOID_INVOICES,
  'Job Register': PERMISSIONS.VIEW_JOBS,
  'Invoice & Job Report': PERMISSIONS.VIEW_REPORTS,
  Income: PERMISSIONS.VIEW_INCOME,
  Settings: PERMISSIONS.VIEW_SETTINGS,
}

// Fine-grained in-page actions that aren't their own sidebar route, keyed by
// the sidebar item title they sit under. Keep these in sync with the
// matching permission check in that page's component (e.g. `canEditReason`
// in views/apps/job-register/List.vue).
const ACTION_ROWS = {
  'Job Register': [
    { title: 'Update Reason', permission: PERMISSIONS.EDIT_JOB_REASON },
  ],
}

export default {
  components: {
    BCard, BTable, BRow, BCol, BFormInput,
  },
  setup() {
    const toast = useToast()
    return { toast }
  },
  data() {
    return {
      dbRoles: [],
      searchQuery: '',
    }
  },
  computed: {
    flatMenuItems() {
      const rows = []
      sidebarItems.forEach(item => {
        if (!item.title) return
        if (item.children) {
          item.children.forEach(child => {
            rows.push({
              menu: `${item.title} > ${child.title}`,
              role: child.role || [],
              permission: MENU_PERMISSIONS[item.title],
            })
          })
        } else {
          rows.push({
            menu: item.title,
            role: item.role || [],
            permission: MENU_PERMISSIONS[item.title],
          })
        }
        (ACTION_ROWS[item.title] || []).forEach(action => {
          rows.push({
            menu: `${item.title} > ${action.title}`,
            role: action.role,
            permission: action.permission,
          })
        })
      })
      return rows
    },
    // Roles ordered by department group, with any role that doesn't match a
    // known group falling back to its own "Other" band rather than disappearing.
    roleGroups() {
      const groups = ROLE_GROUPS.map(g => ({
        ...g,
        roles: g.systemNames
          .map(name => this.dbRoles.find(r => r.UserRoleSystemName === name))
          .filter(Boolean),
      })).filter(g => g.roles.length)

      const grouped = new Set(groups.flatMap(g => g.roles.map(r => r.UserRoleSystemName)))
      const other = this.dbRoles.filter(r => !grouped.has(r.UserRoleSystemName))
      if (other.length) groups.push({ label: 'Other', variant: 'secondary', roles: other })

      return groups
    },
    orderedRoles() {
      return this.roleGroups.flatMap(g => g.roles.map(r => ({ ...r, groupVariant: g.variant })))
    },
    tableFields() {
      return [
        { key: 'menu', label: 'Menu', stickyColumn: true },
        ...this.orderedRoles.map(r => ({
          key: r.UserRoleSystemName,
          label: r.UserRoleName,
          roleVariant: r.groupVariant,
          class: 'text-center',
        })),
      ]
    },
    tableItems() {
      return this.flatMenuItems
        .filter(row => row.menu.toLowerCase().includes(this.searchQuery.toLowerCase()))
        .map(row => {
          const item = { menu: row.menu }
          this.dbRoles.forEach(r => {
            item[r.UserRoleSystemName] = row.permission
              ? permissionsForRole(r.UserRoleSystemName).includes(row.permission)
              : row.role.includes(r.UserRoleSystemName)
          })
          return item
        })
    },
  },
  mounted() {
    if (getUserData().UserRoleSystem !== 'super-admin') {
      this.toast({
        component: ToastificationContent,
        props: {
          title: 'You are not authorized to view this page',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
      this.$router.push({ name: 'dashboard-ecommerce' })
      return
    }
    this.fetchRoles()
  },
  methods: {
    fetchRoles() {
      const userToken = getToken()
      const headers = {
        'Content-Type': 'application/json',
        'X-Token-Access': `Bearer ${userToken}`,
      }
      axios
        .get(process.env.VUE_APP_API_GET_ROLES, { headers })
        .then(response => {
          this.dbRoles = response.data.Payload || []
        })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Error fetching role list',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.matrix-scroll {
  position: relative;
}

::v-deep .roles-matrix {
  th, td {
    white-space: nowrap;
  }

  .group-row th {
    text-align: center;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    border-bottom-width: 2px;
  }
}

.menu-cell {
  .menu-parent {
    font-weight: 500;
  }

  .menu-child {
    color: inherit;
  }
}

.role-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;

  @each $variant, $color in (primary: #213783, success: #28c76f, warning: #ff9f43, danger: #ea5455, secondary: #82868b) {
    &.role-check-#{$variant} {
      color: $color;
      background-color: rgba($color, 0.12);
    }
  }
}
</style>
